/**
 * CRM 화면이 공유하는 enum 라벨과 KST 날짜 계산.
 *
 * 라벨은 설계 §19가 확정한 값이다. 서버가 모르는 값을 보내면 원문을 그대로 보여준다 —
 * 조용히 빈칸으로 만들면 운영자가 데이터가 없는 것으로 읽는다.
 */

export const KST_TIME_ZONE = 'Asia/Seoul'

export const LEAD_STAGE_LABEL: Record<string, string> = {
  NEW_LEAD: '신규 리드',
  CONSULTING: '상담',
  QUALIFICATION: '적격성',
  TERRITORY_ANALYSIS: '상권 분석',
  DISCLOSURE: '정보공개서',
  CONTRACT_PROPOSAL: '계약 제안',
  CONTRACT: '계약',
  HOLD_CLOSED: '보류 · 종결',
}

/**
 * 대시보드 퍼널 표시용 합성 단계(팀 결정 2026-09-01, 축 1-B).
 * 리드 stage가 아니라 접수함 데이터로 만든 리드 이전 단계다.
 */
export const FUNNEL_SYNTHETIC_STAGE_LABEL: Record<string, string> = {
  INTAKE: '신청(접수)',
  BRIEFING_ATTENDED: '설명회 참석',
}

/** 계약 축의 순서. HOLD_CLOSED는 진행 축 밖이라 여기 없다. */
export const LEAD_STAGE_ORDER = [
  'NEW_LEAD',
  'CONSULTING',
  'QUALIFICATION',
  'TERRITORY_ANALYSIS',
  'DISCLOSURE',
  'CONTRACT_PROPOSAL',
  'CONTRACT',
] as const

export const LEAD_PRIORITY_LABEL: Record<string, string> = {
  CONTRACT_IMMINENT: '계약 임박',
  CONTRACT_LIKELY: '계약 유력',
  PROMISING: '유망',
  NORMAL: '보통',
  UNCLASSIFIED: '미분류',
}

export const LEAD_PRIORITY_ORDER = [
  'CONTRACT_IMMINENT',
  'CONTRACT_LIKELY',
  'PROMISING',
  'NORMAL',
  'UNCLASSIFIED',
] as const

export const ATTENDANCE_STATUS_LABEL: Record<string, string> = {
  ATTENDED: '참석',
  SCHEDULED: '예정',
  NO_SHOW: '불참',
  NONE: '없음',
}

/** 설계 §19.1. OTHER는 note 필수다. */
export const HOLD_CLOSE_REASON_LABEL: Record<string, string> = {
  NO_RESPONSE: '미연락 · 무응답',
  POSTPONED: '시기 연기',
  COMPETITOR: '타사 선택',
  FINANCIAL: '자금 문제',
  LOCATION: '입지 문제',
  FAMILY: '가족 반대',
  PERSONAL: '개인 사정',
  NOT_QUALIFIED: '자격 미달',
  OTHER: '기타',
}

/** 설계 §19.2. */
export const BUDGET_BAND_LABEL: Record<string, string> = {
  UNDER_50M: '5천만원 미만',
  RANGE_50M_100M: '5천만원~1억원',
  RANGE_100M_200M: '1억원~2억원',
  RANGE_200M_300M: '2억원~3억원',
  OVER_300M: '3억원 초과',
  UNDECIDED: '미정',
}

/** 설계 §19.3. */
export const SPACE_STATUS_LABEL: Record<string, string> = {
  OWNED: '보유 공간 있음',
  LEASED: '임대 계약 완료',
  SEARCHING: '물색 중',
  NEGOTIATING: '계약 협의 중',
  UNDECIDED: '미정',
}

/** 설계 §19.7. QUALIFIED가 아니면 note가 필수다. */
export const QUALIFICATION_RESULT_LABEL: Record<string, string> = {
  QUALIFIED: '적격',
  CONDITIONAL: '조건부',
  NOT_QUALIFIED: '부적격',
}

export const INBOX_STATUS_LABEL: Record<string, string> = {
  PENDING: '대기',
  CONVERTED: '전환 완료',
}

export const SOURCE_TYPE_LABEL: Record<string, string> = {
  BRIEFING: '사업설명회',
  FRANCHISE: '가맹 문의',
  MANUAL: '수동 등록',
}

/** 접수함(설명회 회차 단위) 참석 여부. 리드의 ATTENDANCE_STATUS_LABEL과 달리 UNKNOWN=미기록이 있다. */
export const INTAKE_ATTENDANCE_LABEL: Record<string, string> = {
  ATTENDED: '참석',
  NO_SHOW: '불참',
  UNKNOWN: '미기록',
}

/** 설명회 회차 방식. 원장(lc_briefing_application)의 값과 같다. 검수에서 말한 "참석 유형"이 이것이다. */
/** 접수 고객 구분 — 설명회만 · 문의만 · 둘 다 (+ 수동·기타). */
export const INTAKE_CUSTOMER_KIND_LABEL: Record<string, string> = {
  BRIEFING_ONLY: '설명회만',
  FRANCHISE_ONLY: '문의만',
  BOTH: '둘 다',
  OTHER: '기타',
}

/** 접수 고객 참석 요약 — 한 번이라도 참석했으면 참석, 미래 회차 신청이 있으면 예정. */
export const ATTENDANCE_SUMMARY_LABEL: Record<string, string> = {
  ATTENDED: '참석',
  SCHEDULED: '예정',
  NO_SHOW: '불참',
  UNKNOWN: '미확인',
}

export const SESSION_MODE_LABEL: Record<string, string> = {
  offline: '오프라인',
  online: '온라인',
}

export const APPLICATION_STATUS_LABEL: Record<string, string> = {
  ACTIVE: '유효',
  CANCELLED: '취소',
}

export const CONSULTATION_CHANNEL_LABEL: Record<string, string> = {
  PHONE: '전화',
  IN_PERSON: '대면',
  ONLINE: '온라인',
  VOICE_SUMMARY: '통화 요약',
}

export const TASK_STATUS_LABEL: Record<string, string> = {
  OPEN: '진행 중',
  DONE: '완료',
  CANCELLED: '취소',
}

export const MERGE_REVIEW_STATUS_LABEL: Record<string, string> = {
  PENDING: '검토 대기',
  APPROVED: '승인',
  REJECTED: '거절',
}

export const AUDIT_ACTION_LABEL: Record<string, string> = {
  CREATE: '생성',
  UPDATE: '정보 수정',
  STAGE_CHANGE: '단계 변경',
  PRIORITY_CHANGE: '우선순위 변경',
  QUALIFICATION_DECIDE: '적격성 판정',
  HOLD_CLOSE: '보류 · 종결',
  CONVERT_TO_LEAD: '리드 전환',
  LEAD_CONTRACTED: '계약 전환',
  MERGE: '병합',
  SPLIT: '분리',
  CANCEL: '취소',
  SESSION_CHANGE: '회차 변경',
  DOCUMENT_ADD: '문서 등록',
  DOCUMENT_DELETE: '문서 삭제',
}

export const LEAD_DOCUMENT_TYPE_LABEL: Record<string, string> = {
  QUALIFICATION: '적격성 근거 자료',
  DISCLOSURE: '정보공개서 원본',
  CONTRACT: '계약서 원본',
}

export const MERGE_REVIEW_REASON_LABEL: Record<string, string> = {
  PHONE_TYPO: '전화번호 오타 의심',
  PHONE_CHANGED_OR_FAMILY: '번호 변경 또는 가족',
  MULTI_CANDIDATE: '후보 다수',
  IS_TEST_CONFLICT: '테스트 플래그 충돌',
}

export function labelOf(map: Record<string, string>, value: string | null | undefined): string {
  if (!value) return '-'
  return map[value] ?? value
}

/** ISO 시각의 KST 달력 날짜를 `YYYY-MM-DD`로. 런타임 타임존과 무관하게 같은 값을 낸다. */
export function kstDateKey(iso: string | Date): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: KST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

function dayNumber(key: string): number {
  const [year, month, day] = key.split('-').map(Number)
  return Date.UTC(year, month - 1, day) / 86_400_000
}

/**
 * 체류일 = KST 오늘 − 기준 시각의 KST 날짜 (설계 §11.1).
 *
 * 시각 차이가 아니라 달력 날짜 차이다. 어제 23시와 오늘 01시는 2시간 차이지만 체류일은 1일이다.
 */
export function daysSinceKst(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return null
  return dayNumber(kstDateKey(now)) - dayNumber(kstDateKey(then))
}

export type StaleLevel = 'normal' | 'warning' | 'danger'

/** 31~60일 경고, 61일 이상 위험 (설계 §11.1). */
export function staleLevel(days: number | null): StaleLevel {
  if (days === null) return 'normal'
  if (days >= 61) return 'danger'
  if (days >= 31) return 'warning'
  return 'normal'
}

/** 색만으로 상태를 전달하지 않도록 텍스트도 함께 낸다 (packages/ui/DESIGN.md §6). */
export const STALE_LEVEL_TEXT: Record<StaleLevel, string> = {
  normal: '',
  warning: '지연',
  danger: '장기 정체',
}

export function formatKstDateTime(iso: string | null | undefined): string {
  if (!iso) return '-'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('ko-KR', {
    timeZone: KST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatKstDate(iso: string | null | undefined): string {
  if (!iso) return '-'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('ko-KR', {
    timeZone: KST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

/** `YYYY-MM-DD` 같은 날짜 전용 값. 타임존을 붙이면 하루가 밀린다. */
export function formatLocalDate(value: string | null | undefined): string {
  return value ?? '-'
}

/**
 * 저장된 숫자열(`01012345678`)을 읽기 좋은 `010-1234-5678`로 만든다.
 *
 * 가리지 않는다. 설계 §19.5의 개인정보 장치는 CSV `includeOriginal`과 그 감사 로그이지
 * 화면 표시가 아니며, 같은 `LC_CRM` 권한으로 병합 검토 화면·기본 CSV·API 응답에서 이미
 * 전체 번호가 보인다. 화면만 가리면 전화를 걸어야 하는 운영자만 불편해진다.
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '-'
  const digits = phone.replace(/\D/g, '')
  // 서울 지역번호만 두 자리다. 3자리로 끊으면 02-1234-5678이 021-234-5678이 된다.
  if (digits.startsWith('02') && (digits.length === 9 || digits.length === 10)) {
    return `02-${digits.slice(2, -4)}-${digits.slice(-4)}`
  }
  if (digits.length === 10 || digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, -4)}-${digits.slice(-4)}`
  }
  return phone
}

export function formatKrw(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '-'
  return `${amount.toLocaleString('ko-KR')}원`
}

/**
 * 전환율 표시. 1.0을 넘는 값을 잘라내지 않는다 — 단계를 건너뛴 리드가 있으면 뒤 단계 도달
 * 인원이 더 많아질 수 있고, 그건 계산 오류가 아니라 운영 실태다.
 */
export function formatRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '-'
  return `${(rate * 100).toFixed(1)}%`
}
